// OP-3605 — Regras puras da transferência, espelho de `domain/solicitacao.ts`
// (transições, `podeIncluirNaTransferencia`, conferência) e de
// `podeAprovarTransferencia` em `domain/cadastro.ts`. Gates visuais.
import {
  Controle, DecisaoConferencia, EventoTransferencia, LadoTransferencia, ReservaDocumento, StatusTransferencia, TransferenciaItem, UnidadeRow,
} from '../../../../types/estoque'
import { faltaMotivo } from '../formato'
import { rotuloDecisao } from '../conferencia'

export const STATUS_TRANSFERENCIA_UI: Record<StatusTransferencia, { bg: string; color: string; label: string }> = {
  SOLICITADA: { bg: '#DBEAFE', color: '#1E40AF', label: 'Solicitada' },
  APROVADA: { bg: '#D1FAE5', color: '#065F46', label: 'Aprovada' },
  EM_SEPARACAO: { bg: '#E0E7FF', color: '#3730A3', label: 'Em separação' },
  EM_TRANSITO: { bg: '#FEF3C7', color: '#92400E', label: 'Em trânsito' },
  RECEBIDA: { bg: '#FED7AA', color: '#9A3412', label: 'Recebida (a conferir)' },
  CONFERIDA: { bg: '#D1FAE5', color: '#065F46', label: 'Conferida' },
  CANCELADA: { bg: '#F3F4F6', color: '#374151', label: 'Cancelada' },
}

/** Espelho da tabela de transições do domínio. */
export const TRANSICOES_TRANSFERENCIA: Record<StatusTransferencia, StatusTransferencia[]> = {
  SOLICITADA: ['APROVADA', 'CANCELADA'],
  APROVADA: ['EM_SEPARACAO', 'CANCELADA'],
  EM_SEPARACAO: ['EM_TRANSITO', 'CANCELADA'],
  // Depois de despachada não há cancelar: o material saiu e ainda não chegou.
  EM_TRANSITO: ['RECEBIDA'],
  RECEBIDA: ['CONFERIDA'],
  CONFERIDA: [],
  CANCELADA: [],
}

export type AcaoTransferencia = 'APROVAR' | 'SEPARAR' | 'DESPACHAR' | 'RECEBER' | 'CONFERIR' | null

/** A ação que leva a transferência adiante, uma por status. */
export function acaoDoStatusTransferencia(status: StatusTransferencia): AcaoTransferencia {
  switch (status) {
    case 'SOLICITADA': return 'APROVAR'
    case 'APROVADA': return 'SEPARAR'
    case 'EM_SEPARACAO': return 'DESPACHAR'
    case 'EM_TRANSITO': return 'RECEBER'
    case 'RECEBIDA': return 'CONFERIR'
    default: return null
  }
}

export const ladoQueAprova = (quemPediu: LadoTransferencia | null | undefined): LadoTransferencia => (quemPediu === 'DESTINO' ? 'ORIGEM' : 'DESTINO')

export function impedimentoDoLado(
  acao: AcaoTransferencia,
  t: { noOrigem?: boolean; noDestino?: boolean; origemNome: string; destinoNome: string; ladoSolicitante?: LadoTransferencia },
): string | null {
  if (acao === 'APROVAR') {
    if (ladoQueAprova(t.ladoSolicitante) === 'ORIGEM') {
      return t.noOrigem === false ? `O destino pediu: aprovar cabe à praça de origem, dona do equipamento (${t.origemNome}).` : null
    }
    if (t.noDestino === false) return `Aprovar cabe à praça de destino (${t.destinoNome}).`
  }
  if ((acao === 'RECEBER' || acao === 'CONFERIR') && t.noDestino === false) {
    return `Receber e conferir cabem à praça de destino (${t.destinoNome}).`
  }
  if ((acao === 'SEPARAR' || acao === 'DESPACHAR') && t.noOrigem === false) {
    return `Separar e despachar cabem à praça de origem (${t.origemNome}).`
  }
  return null
}

export function impedimentoAprovarPorAutoria(criadoPorId: number | null | undefined, usuarioId: number | null): string | null {
  if (criadoPorId == null || usuarioId == null) return null
  return Number(criadoPorId) === Number(usuarioId)
    ? 'Você abriu esta transferência: quem abre não aprova. Peça a aprovação a outra pessoa da coordenação.'
    : null
}

export const impedimentoConfirmarCancelamento = (motivo: string): string | null =>
  faltaMotivo(motivo, 'Escreva o motivo do cancelamento: ele fica no histórico da transferência.')

const ACAO_DO_EVENTO: Record<StatusTransferencia, string> = {
  SOLICITADA: 'Aberta',
  APROVADA: 'Aprovada',
  EM_SEPARACAO: 'Separação iniciada',
  EM_TRANSITO: 'Despachada',
  RECEBIDA: 'Recebida',
  CONFERIDA: 'Conferida',
  CANCELADA: 'Cancelada',
}

export function textoDoEvento(e: Pick<EventoTransferencia, 'statusPara' | 'usuarioNome' | 'inferido'> & Partial<Pick<EventoTransferencia, 'tipo' | 'motivo'>>): string {
  if (e.tipo === 'NOTA') return `Nota: ${e.motivo ?? ''}`
  const acao = ACAO_DO_EVENTO[e.statusPara] ?? e.statusPara
  if (e.usuarioNome) return `${acao} por ${e.usuarioNome}`
  return e.inferido ? `${acao} (autor não registrado antes de 25/09/2026)` : acao
}

export function desdeDaTransferencia(
  t: { criadoEm: string; aprovadoEm?: string | null; historico?: Array<Pick<EventoTransferencia, 'statusPara' | 'criadoEm'> & Partial<Pick<EventoTransferencia, 'tipo'>>> },
): { rotulo: string; em: string } {
  const eventos = (t.historico ?? []).filter((e) => e.tipo !== 'NOTA')
  const ultimo = eventos.length ? eventos[eventos.length - 1] : null
  if (ultimo) return { rotulo: (ACAO_DO_EVENTO[ultimo.statusPara] ?? ultimo.statusPara).toLowerCase(), em: ultimo.criadoEm }
  return t.aprovadoEm ? { rotulo: 'aprovada', em: t.aprovadoEm } : { rotulo: 'aberta', em: t.criadoEm }
}

export function cancelamentoDaTransferencia<E extends Pick<EventoTransferencia, 'statusPara'> & Partial<Pick<EventoTransferencia, 'tipo'>>>(historico?: E[]): E | null {
  return (historico ?? []).filter((e) => e.tipo !== 'NOTA' && e.statusPara === 'CANCELADA').pop() ?? null
}

/** Cancelar vale enquanto nada saiu (espelho da tabela acima). */
export function chegadaDaLinha(l: Pick<TransferenciaItem, 'controle' | 'quantidade' | 'quantidadeRecebida' | 'condicaoChegada'>): { texto: string; parcial: boolean } {
  if (!l.condicaoChegada) return { texto: '', parcial: false }
  const naoChegou = l.condicaoChegada === 'NAO_RETORNOU' || l.condicaoChegada === 'NAO_CHEGOU'
  const faltou = Number(l.quantidade) - Number(l.quantidadeRecebida)
  if (l.controle === 'QUANTIDADE' && !naoChegou && faltou > 0) {
    return { texto: `Chegou parcial: ${l.quantidadeRecebida} de ${l.quantidade} · ${faltou} ${faltou === 1 ? 'extraviado' : 'extraviados'}`, parcial: true }
  }
  return { texto: rotuloDecisao('CHEGADA', l.condicaoChegada), parcial: false }
}

export const podeCancelarTransferencia = (status: StatusTransferencia): boolean =>
  TRANSICOES_TRANSFERENCIA[status].indexOf('CANCELADA') >= 0

/** A capacidade que cada ação exige, para a tela não oferecer o que a sessão não pode. */
export const CAPACIDADE_DA_ACAO: Record<Exclude<AcaoTransferencia, null>, 'APROVAR_TRANSFERENCIA' | 'TRANSFERIR'> = {
  APROVAR: 'APROVAR_TRANSFERENCIA',
  SEPARAR: 'TRANSFERIR',
  DESPACHAR: 'TRANSFERIR',
  RECEBER: 'TRANSFERIR',
  CONFERIR: 'TRANSFERIR',
}

// ---- Criação ---------------------------------------------------------------

/** Espelho de `validarTransferencia`: origem, destino e pelo menos um item. */
export function impedimentoNovaTransferencia(origemId: number | null, destinoId: number | null, itens: number): string | null {
  if (!origemId || !destinoId) return 'Escolha a praça de origem e a de destino.'
  if (origemId === destinoId) return 'Origem e destino são a mesma praça.'
  if (itens <= 0) return 'Inclua pelo menos um item.'
  return null
}

export function ladoDaCriacao(c: { origemNoEscopo: boolean; destinoNoEscopo: boolean; comPedido: boolean }): LadoTransferencia | null {
  if (c.comPedido) return c.destinoNoEscopo ? 'DESTINO' : null
  if (c.origemNoEscopo) return 'ORIGEM'
  if (c.destinoNoEscopo) return 'DESTINO'
  return null
}

export const IMPEDIMENTO_FORA_DO_ESCOPO = 'A origem ou o destino precisa ser uma praça que você opera.'

export function textoDoLado(lado: LadoTransferencia | null, origemNome: string, destinoNome: string): string | null {
  if (lado === 'ORIGEM') return `Você oferece: quem aprova é ${destinoNome}.`
  if (lado === 'DESTINO') return `Você pede: quem aprova é ${origemNome}, dona do equipamento.`
  return null
}

/** Consumível só sai do que está livre na origem; `livre` nulo = ainda não se sabe, e não barra. */
export function impedimentoQuantidadeConsumivel(quantidade: string, livre: number | null): string | null {
  const texto = String(quantidade ?? '').trim()
  if (!texto) return 'Informe a quantidade.'
  const n = Number(texto)
  if (!Number.isInteger(n) || n <= 0) return 'Informe uma quantidade inteira maior que zero.'
  if (livre != null && n > livre) {
    return livre <= 0 ? 'Nada livre deste item na origem.' : `Só há ${livre} un. ${livre === 1 ? 'livre' : 'livres'} na origem.`
  }
  return null
}

/**
 * Espelho de `podeIncluirNaTransferencia`, na mesma ordem do domínio. A tela
 * só oferece peças que `getEstoqueDisponiveis` já filtrou, mas a lista de
 * equipamentos mostra tudo — e daí vem a tentação de incluir a errada.
 */
export function impedimentoIncluirPeca(u: Pick<UnidadeRow, 'situacao' | 'condicao' | 'cicloVida' | 'localNome' | 'disponivel'>, origemNome: string): string | null {
  if (u.localNome !== origemNome) return 'A peça não está na praça de origem.'
  if (!Number(u.disponivel)) return 'A peça não está disponível (precisa estar em estoque, OK e ativa).'
  return null
}

// ---- Conferência -----------------------------------------------------------

/**
 * Que decisões cabem na linha — espelho de `decisaoCabeNaLinha`.
 *
 * Linha de quantidade só aceita OK e NAO_RETORNOU: avariado e sucata são
 * condição de PEÇA, e saldo não tem condição.
 */
export function decisoesDaLinha(controle: Controle): DecisaoConferencia[] {
  return controle === 'SERIAL'
    ? ['OK', 'AVARIADO', 'SUCATA', 'NAO_RETORNOU']
    : ['OK', 'NAO_RETORNOU']
}

export interface LinhaConferenciaTransferencia {
  decisao: DecisaoConferencia | ''
  quantidadeRecebida: string
  observacao: string
}

/**
 * Primeiro impedimento da conferência, ou null. Espelha
 * `podeFecharConferencia` (toda linha decidida) e
 * `resolverQuantidadeConferida` (consumível com quantidade OMITIDA é erro,
 * zero explícito é zero, nunca acima do que foi enviado).
 */
export function impedimentoConferenciaTransferencia(
  itens: TransferenciaItem[], f: Record<number, LinhaConferenciaTransferencia>,
): string | null {
  if (!itens.length) return 'Esta transferência não tem itens a conferir.'
  for (const l of itens) {
    const x = f[l.id]
    if (!x || !x.decisao) return `Decida o que aconteceu com ${l.serial ?? l.itemNome}.`
    if (decisoesDaLinha(l.controle).indexOf(x.decisao) < 0) {
      return `${l.itemNome}: "${x.decisao}" não cabe numa linha de quantidade.`
    }
    if (l.controle === 'QUANTIDADE' && x.decisao !== 'NAO_RETORNOU') {
      const q = x.quantidadeRecebida.trim()
      if (q === '') return `${l.itemNome}: informe a quantidade recebida (zero é zero, em branco não).`
      const n = Number(q)
      if (!Number.isInteger(n) || n < 0 || n > Number(l.quantidade)) {
        return `${l.itemNome}: quantidade entre 0 e ${l.quantidade}.`
      }
    }
  }
  return null
}

/**
 * O payload da conferência.
 *
 * `itemId` aqui é o id da LINHA (`est_transferencia_item.id`): o service
 * indexa por ele. Mandar o id do catálogo devolve `ITEM_FORA_DA_TRANSFERENCIA`.
 */
export function montarConferenciaTransferencia(itens: TransferenciaItem[], f: Record<number, LinhaConferenciaTransferencia>) {
  return itens.map((l) => {
    const x = f[l.id]
    const base = { itemId: l.id, decisao: x.decisao as DecisaoConferencia, observacao: x.observacao.trim() || null }
    if (l.controle === 'SERIAL') return { ...base, quantidadeRecebida: x.decisao === 'NAO_RETORNOU' ? 0 : 1 }
    return { ...base, quantidadeRecebida: x.decisao === 'NAO_RETORNOU' ? 0 : Number(x.quantidadeRecebida) }
  })
}

/**
 * O que não chegou vira EXTRAVIO automático na conferência — espelho de
 * `faltaDaConferencia`. Serializada não entra: quem não voltou já é extravio
 * pela própria decisão.
 */
export function faltaDaConferencia(l: Pick<TransferenciaItem, 'controle' | 'quantidade'>, recebida: number): number {
  if (l.controle === 'SERIAL') return 0
  return Math.max(0, Number(l.quantidade) - recebida)
}

/**
 * OP-3619 — o que impede aprovar, dito ANTES do clique.
 *
 * A aprovação reserva o consumível, e a guarda do service recusa quando não há
 * livre na origem (`exigirLivreConsumiveis`). Sem aviso, o operador vê um botão
 * vivo que não faz nada — foi o N1 do reteste. O detalhe agora traz `livreNaOrigem`
 * e quem reservou; aqui isso vira frase.
 *
 * Duas fontes seguram saldo, e trocá-las na mensagem é pior que o silêncio:
 * transferência aprovada se despacha ou cancela; pedido separado se expede ou
 * tem a separação reduzida.
 */
export interface FaltaDeSaldo {
  item: string
  pedido: number
  livre: number
  reservas: ReservaDocumento[]
  ocultas: number
}

type LinhaDeSaldo = Pick<TransferenciaItem,
  'itemId' | 'itemNome' | 'unidadeId' | 'quantidade' | 'livreNaOrigem' | 'pedidoDoItem' | 'reservas' | 'reservasOcultas'>

/**
 * Duas linhas do mesmo consumível somam na hora de reservar — é o que o
 * service faz antes de aprovar. Por isso a comparação é por ITEM, e uma linha
 * por item entra na lista, não uma por linha da transferência.
 */
export function faltasDeSaldo(itens: LinhaDeSaldo[]): FaltaDeSaldo[] {
  const vistos = new Set<number>()
  const faltas: FaltaDeSaldo[] = []
  for (const i of itens) {
    if (i.unidadeId != null || i.livreNaOrigem == null) continue
    const itemId = Number(i.itemId)
    if (vistos.has(itemId)) continue
    vistos.add(itemId)
    const pedido = i.pedidoDoItem != null ? Number(i.pedidoDoItem) : Number(i.quantidade)
    const livre = Number(i.livreNaOrigem)
    if (pedido <= livre) continue
    faltas.push({
      item: i.itemNome, pedido, livre, reservas: i.reservas ?? [], ocultas: Number(i.reservasOcultas) || 0,
    })
  }
  return faltas
}

const SAIDA: Record<ReservaDocumento['tipo'], string> = {
  TRANSFERENCIA: 'Despache ou cancele aquela primeiro.',
  SOLICITACAO: 'Expedir o pedido ou reduzir a separação dele libera o saldo.',
}

/** A frase do impedimento, com o documento nomeado quando há um só. */
export function textoFaltaDeSaldo(faltas: FaltaDeSaldo[], origem: string): string | null {
  if (faltas.length === 0) return null
  const f = faltas[0]
  const resto = faltas.length > 1 ? ` (e mais ${faltas.length - 1} ${faltas.length === 2 ? 'item' : 'itens'})` : ''
  const cabeca = `Não há saldo livre em ${origem}: ${f.item} tem ${f.livre} livre${f.livre === 1 ? '' : 's'} e o pedido é de ${f.pedido}${resto}.`
  if (f.reservas.length === 1) {
    const r = f.reservas[0]
    const quem = r.tipo === 'TRANSFERENCIA' ? 'A transferência' : 'O pedido'
    return `${cabeca} ${quem} ${r.numero} reservou ${r.quantidade}. ${SAIDA[r.tipo]}`
  }
  if (f.reservas.length > 1) {
    const lista = f.reservas.map((r) => `${r.numero} (${r.quantidade})`).join(', ')
    const mais = f.ocultas > 0 ? ` e mais ${f.ocultas}` : ''
    return `${cabeca} Já reservaram este material: ${lista}${mais}.`
  }
  return cabeca
}
